import React, { useMemo } from 'react';
import styled from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HashRouter, Route, Switch } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from 'components/store/store';
import { AppHeader, SideMenu } from 'components';
import DailyShowingSettingsModal from 'components/ShowingSettingsModal/DailyShowingSettingsModal';
import { useAppRedirect, useMixpanel } from 'hooks';
import {
  AgentsContextProvider,
  AuthContextProvider,
  AuthContext,
  AdminContextProvider,
  ContactDrawerContextProvider,
  ExternalAuthContextProvider,
  LayoutContextProvider,
  GlobalModalContextProvider,
  AdminContext,
  UniversalSearchContextProvider,
  CrossOrgAnsweringContextProvider,
  UserFavoritesContextProvider,
  ListingLabelContextProvider,
  TeamContextProvider,
  ContextualChatContextProvider,
} from 'contexts';
import { AppointmentActionModalContextProvider } from 'contexts/modal/AppointmentActionModalContext';
import { featureEnabled } from 'utils';

import { UserRoles, UserFeatures } from '@bb/users';
import {
  SupraSystemSubscriptionStatus,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types/SupraSystemSubscription';
import { Feature } from '@bb/automaton';
import { DialogStore } from 'hooks/dialogStore/DialogStore';

import {
  AppcuesLoader,
  AssumeNavbar,
  AuthRedirects,
  Chat,
  Content,
  AppErrorBoundary,
  Footer,
  GlobalBanner,
  PageRoute,
  FeatureBanner,
  FeatureRedirects,
  FeatureProtectedRoute,
} from './components';

import {
  AdminPage,
  AnnouncementEditPage,
  AnnouncementListPage,
  LiveAppointmentDesk,
  AnalyticsPage,
  NeedsPage,
  AngularPages,
  AnsweringSettingsPage,
  ManageAnsweringStaff,
  MessagingInboxPage,
  MyBrokeragePage,
  MyBusinessPage,
  NotificationCentrePage,
  NotificationSettingsPage,
  OrgSettingsPage,
  TemplateManagerPage,
  CreateListingPage,
  EditListingPage,
  DocumentsPage,
  ManageUsersPage,
  NewGroupsPage,
  DevPage,
  ContactsPage,
  TourEditPage,
  UserViewPage,
  Error403TeamAccountPage,
  BookMultipleExternalAppointments,
  ShowingsCalendarPage,
  InternalShowingDetailsPage,
  ExternalShowingDetailsPage,
  RetsShowingDetailsPage,
  AnsweringSearchPage,
  PropertyUpdatePage,
  MarketInsight,
  AccessActivityPage,
  QuickLockboxAssignmentPage,
  QuickLockboxAssignmentActivityPage,
  MLSStaffCoopPage,
  AssetsPage,
  ManagedAccessDetailsPage,
} from './pages';

import Modals from './components/modals';
import ContactDrawer from './ContactDrawer';
import StreamChatRouteHandler from './StreamChatRouteHandler';

import '../styles/vendor.less';
import '../styles/main.less';
import '../styles/angular.less';

import AddUsersToTeamsQAPage from './pages/AddUsersToTeamsQAPage';
import AddOrUpdateTeamsQAPage from './pages/AddOrUpdateTeamsQAPage';
import AppointmentBookingInternalPage from './pages/AppointmentBookingInternalPage';
import BillingInvoicesPage from './pages/BillingInvoicesPage';
import TeamSettingsPage from './pages/TeamSettingsPage';
import OnboardingToolsPage from './pages/OnboardingToolsPage';
import NoFeatureFlagPage from './pages/NoFeatureFlagPage';
import QAPages from './pages/QAPages';
import MLSUserPage from './pages/MLSUserPage';
import LockBoxesActivityPage from './pages/LockboxesActivityPage';
import MLSStaffPage from './pages/MLSStaffPages';
import SupraLockBoxDetailsPage from './pages/MLSLockboxPage';
import LockboxesPage from './pages/LockboxesPage';
import LockboxAccessLogPage from '../app/ManageLockboxPage/LockboxAccessLogPage';
import NonMemberRestrictionPage from './pages/NonMemberRestictionPage';
import ContractsPage from './pages/ContractsPage';
import { mlsStaffFeaturePageMap } from './constants';
import { ActivationFeeContextProvider } from './pages/MLSStaffPages/ActivationFeeContext';
import coopOrganizationsPage from './pages/CoopOrganizationsPage';
import PayNowBanner from './components/PayNowBanner';
import SubscriptionsPaymentsPage from './pages/SubscriptionsPaymentsPage';
import AnsweringServicePage from './pages/AnsweringServicePage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

export const InternalRouter = () => {
  const isFinalPath = useAppRedirect('/app/', '');

  return (
    <HashRouter>
      {isFinalPath && (
        <ReduxProvider store={store}>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools initialIsOpen={false} panelPosition="right" />
            <AuthContextProvider>
              <LayoutContextProvider entryRoute="internal">
                <ContextualChatContextProvider>
                  <ListingLabelContextProvider>
                    <UniversalSearchContextProvider>
                      <CrossOrgAnsweringContextProvider>
                        <UserFavoritesContextProvider>
                          <AdminContextProvider>
                            <AgentsContextProvider>
                              <ActivationFeeContextProvider>
                                <ContactDrawerContextProvider>
                                  <GlobalModalContextProvider>
                                    <DialogStore />
                                    <Modals />
                                    <Routes />
                                    <Chat />
                                  </GlobalModalContextProvider>
                                </ContactDrawerContextProvider>
                              </ActivationFeeContextProvider>
                            </AgentsContextProvider>
                          </AdminContextProvider>
                        </UserFavoritesContextProvider>
                      </CrossOrgAnsweringContextProvider>
                    </UniversalSearchContextProvider>
                  </ListingLabelContextProvider>
                </ContextualChatContextProvider>
              </LayoutContextProvider>
            </AuthContextProvider>
          </QueryClientProvider>
        </ReduxProvider>
      )}
    </HashRouter>
  );
};

export const ExternalRouter = () => {
  const isFinalPath = useAppRedirect('/app/external', '/external');

  return (
    <HashRouter>
      {isFinalPath && (
        <ExternalAuthContextProvider>
          <LayoutContextProvider entryRoute="external">
            <ListingLabelContextProvider>
              <UniversalSearchContextProvider>
                <ContactDrawerContextProvider isExternal>
                  <AppointmentActionModalContextProvider isExternal>
                    <Switch>
                      <Route>
                        <Routes isExternal />
                      </Route>
                    </Switch>
                  </AppointmentActionModalContextProvider>
                </ContactDrawerContextProvider>
              </UniversalSearchContextProvider>
            </ListingLabelContextProvider>
          </LayoutContextProvider>
        </ExternalAuthContextProvider>
      )}
    </HashRouter>
  );
};

const Routes: React.FC<{ isExternal?: boolean }> = ({ isExternal }) => {
  const { auth, isBrokerBay } = React.useContext(AuthContext);
  const { isAdminAccessRestricted } = React.useContext(AdminContext) || {};

  const useEnhancedAnswering = React.useMemo(
    () => auth?.org.type === 'answering' || auth?.org.type === 'mlsStaff',
    [auth],
  );

  // Will return true if the the keyholder FF is off OR
  // if there is no subscription OR
  // if the subscription status is not deactivated and suspended
  const shouldDisplayAccessFeatures = useMemo(
    () =>
      !featureEnabled('keyholderBillingFeature') ||
      !auth?.supraSubscription ||
      (auth.supraSubscription.status !==
        SupraSystemSubscriptionStatus.deactivated &&
        auth.supraSubscription.status !==
          SupraSystemSubscriptionStatus.suspended),
    [auth?.supraSubscription],
  );

  useMixpanel({ trackDailySession: true });

  const canEditListing = React.useMemo(
    () => auth?.org.country === 'CAN' || auth?.parentOrgType === 'mlsStaff',
    [auth],
  );

  const isRestrictedNonMemberUser = useMemo(
    () =>
      !!auth?.supraSubscription &&
      // All Managed Access subscribers
      (auth?.supraSubscription?.type ===
        SupraSystemSubscriptionType.MANAGED_ACCESS ||
        // Limited subscribers with inactive subscriptions
        (auth?.supraSubscription?.type ===
          SupraSystemSubscriptionType.LIMITED &&
          auth?.supraSubscription.status ===
            SupraSystemSubscriptionStatus.unsubscribed) ||
        // Limited subscribers without scheduling services
        (auth?.supraSubscription?.type ===
          SupraSystemSubscriptionType.LIMITED &&
          !auth?.features?.includes(UserFeatures['Scheduling Services']))),
    [auth],
  );

  const shouldShowEditListing = featureEnabled(
    'supraHideAdminFunctionalityFeature',
  )
    ? (auth?.org.features.includes('SUPRA_MVP') && canEditListing) ||
      !auth?.org.features.includes('SUPRA_MVP')
    : true;

  const OrgAdminFeatureProtectedRoute = featureEnabled('orgAdminFeature')
    ? FeatureProtectedRoute
    : PageRoute;

  const isMLSWithMissingFeatures = React.useMemo(
    () =>
      auth?.org.type === 'mlsStaff' &&
      !auth?.org.features.some(f => {
        let featurePages = Object.keys(mlsStaffFeaturePageMap);
        if (!featureEnabled('supraBillingApiFeature')) {
          featurePages = featurePages.filter(
            page => page !== Feature.MLS_STAFF_VIEW_CONTRACTS,
          );
        }
        return featurePages.includes(f);
      }),
    [auth?.org],
  );

  return (
    <AppErrorBoundary>
      {
        // Any time the URL changes, send it through these series of checks
        // to ensure whether we should redirect the user from a restricted page.
      }
      {!isExternal && <AppcuesLoader />}
      <AuthRedirects
        isRestrictedNonMemberUser={isRestrictedNonMemberUser}
        isMLSWithMissingFeatures={isMLSWithMissingFeatures}
      />
      <FeatureRedirects>
        <Route>
          <AssumeNavbar />
          {!isExternal && <FeatureBanner />}
          {!isExternal && <GlobalBanner />}
          {!isExternal && <DailyShowingSettingsModal />}
          {isRestrictedNonMemberUser ? (
            <Route
              exact
              path="/non_member_restricted"
              component={NonMemberRestrictionPage}
            />
          ) : (
            <StyledWrapper>
              <SideMenu />
              <div>
                <AppHeader isExternal={isExternal} />
                <Content>
                  {!isExternal && <PayNowBanner />}
                  <Switch>
                    {!featureEnabled('analyticsEnhanceFeature') && (
                      <FeatureProtectedRoute
                        requiredFeatures={['ANALYTICS']}
                        title="Analytics"
                        path="/analytics"
                        component={AnalyticsPage}
                      />
                    )}
                    {featureEnabled('analyticsEnhanceFeature') && (
                      <FeatureProtectedRoute
                        requiredFeatures={['ANALYTICS']}
                        title="Analytics"
                        path="/analytics/market"
                        render={props => (
                          <AnalyticsPage
                            key="market"
                            pageType="market"
                            {...props}
                          />
                        )}
                      />
                    )}
                    {featureEnabled('analyticsEnhanceFeature') && (
                      <FeatureProtectedRoute
                        requiredFeatures={['ANALYTICS']}
                        title="Analytics"
                        path="/analytics/dashboard"
                        render={props => (
                          <AnalyticsPage
                            key="dashboard"
                            pageType="dashboard"
                            {...props}
                          />
                        )}
                      />
                    )}
                    {featureEnabled('analyticsTableauFeaturePhase', 1) && (
                      <FeatureProtectedRoute
                        requiredFeatures={['SHOWING_INSIGHTS']}
                        title="Showing Insights"
                        path="/analytics/insight"
                        component={MarketInsight}
                      />
                    )}
                    {featureEnabled('removeAngularFeature') && (
                      <PageRoute
                        title="Brokerage Announcements"
                        path="/announcement/list"
                        component={AnnouncementListPage}
                      />
                    )}
                    {featureEnabled('removeAngularFeature') && (
                      <PageRoute
                        title="Brokerage Announcements"
                        path={[
                          '/announcement/new',
                          '/announcement/:announcementId/edit',
                        ]}
                        render={props => (
                          <AnnouncementEditPage auth={auth} {...props} />
                        )}
                      />
                    )}
                    {featureEnabled('removeAngularFeature') && (
                      <PageRoute
                        title="Brokerage Files"
                        path="/assets"
                        render={props => <AssetsPage auth={auth} {...props} />}
                      />
                    )}
                    <PageRoute
                      title="Template Manager"
                      path="/template/manage"
                      render={() => (
                        <TeamContextProvider auth={auth}>
                          <TemplateManagerPage />
                        </TeamContextProvider>
                      )}
                    />
                    <PageRoute
                      title="Notifications"
                      exact
                      path="/notifications"
                      component={NotificationSettingsPage}
                    />
                    <PageRoute
                      title="Notifications"
                      path={['/notification/list/:notificationId?']}
                      component={NotificationCentrePage}
                    />
                    <PageRoute
                      title="Notifications"
                      exact
                      path="/unsubscribe/:unsubscribeId"
                      render={() => (
                        <NotificationSettingsPage
                          mode="unsubscribe"
                          isExternal
                        />
                      )}
                    />
                    <PageRoute
                      title="Notifications"
                      exact
                      path="/brokerbay_admin/org/:orgId/notifications/:location"
                      render={() => <NotificationSettingsPage mode="org" />}
                    />
                    <PageRoute
                      title="Messaging Inbox"
                      path={['/inbox/:convoId?', '/repinbox/:userId']}
                      component={MessagingInboxPage}
                    />
                    <Route
                      path="/streamchat/:channelId?/:messageId?"
                      component={StreamChatRouteHandler}
                    />
                    <Route
                      path={[
                        '/needs/new',
                        '/needs/:needId/view',
                        '/needs/:needId/edit',
                        '/needs/:needId/upload',
                      ]}
                      component={NeedsPage}
                    />
                    {/* This is separate because it the default page for admins,
                  and needs to be redirected specially if it is missing the required feature */}
                    <FeatureProtectedRoute
                      requiredFeatures={['MY_BROKERAGE']}
                      exact
                      path="/listing/list/brokerage/:pageNumber"
                      component={MyBrokeragePage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={['MY_BROKERAGE']}
                      exact
                      path={['/open_houses', '/needs/:pageNumber']}
                      component={MyBrokeragePage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={['MY_BROKERAGE']}
                      title="Create a Listing"
                      exact
                      path="/listing/new"
                      component={CreateListingPage}
                    />
                    {shouldShowEditListing && (
                      <PageRoute
                        title="Edit Listing"
                        exact
                        path="/listing/:listingId/edit"
                        component={EditListingPage}
                      />
                    )}
                    <FeatureProtectedRoute
                      title="Documents"
                      requiredFeatures={['CREATE_LISTING']}
                      exact
                      path={[
                        '/listing/:listingId/documents',
                        '/listing/:listingId/documents/:documentId',
                      ]}
                      component={DocumentsPage}
                    />
                    <PageRoute
                      title="Showings"
                      exact
                      path={['/listing/:listingId/showings']}
                      render={props => (
                        <ShowingsCalendarPage
                          {...props}
                          key="listing"
                          calendarType="listing"
                        />
                      )}
                    />
                    <PageRoute
                      title="Access Activity"
                      exact
                      path={['/listing/:listingId/access_activity']}
                      render={props => {
                        const listingId = props?.match?.params?.listingId;

                        if (!listingId) return null;
                        return <AccessActivityPage listingId={listingId} />;
                      }}
                    />
                    <Route
                      exact
                      path="/my_business"
                      component={MyBusinessPage}
                    />
                    <PageRoute
                      title="Showings"
                      exact
                      path={[
                        '/my_business/showings/buyer',
                        '/my_business/showings/buyer/:page',
                      ]}
                      render={props => (
                        <ShowingsCalendarPage
                          {...props}
                          key="buyer"
                          calendarType="buyer"
                        />
                      )}
                    />
                    <PageRoute
                      title="Showings"
                      exact
                      path={[
                        '/my_business/showings/listing',
                        '/my_business/showings/listing/:page',
                      ]}
                      render={props => (
                        <ShowingsCalendarPage
                          {...props}
                          key="la"
                          calendarType="la"
                        />
                      )}
                    />
                    {featureEnabled('showingsMadeEasyPhase2Feature') && (
                      <PageRoute
                        title="Book Showing"
                        exact
                        // This will overwrite the angular page
                        path={['/listing/:listingId/appointments/book']}
                        component={AppointmentBookingInternalPage}
                      />
                    )}
                    {
                      <PageRoute
                        title="Showing Request"
                        exact
                        path={['/my_business/showing/:appointmentId']}
                        render={() => <InternalShowingDetailsPage />}
                      />
                    }
                    {
                      <FeatureProtectedRoute
                        requiredFeatures={['SUPRA_MVP']}
                        title="Managed Access Details"
                        exact
                        path={[
                          '/my_business/managed_access/:managedAccessKdpId',
                        ]}
                        render={() => <ManagedAccessDetailsPage />}
                      />
                    }
                    {
                      <PageRoute
                        title="Property Update"
                        exact
                        path={['/listing/property_update/:listingId']}
                        component={PropertyUpdatePage}
                      />
                    }
                    <FeatureProtectedRoute
                      requiredFeatures={['MY_BROKERAGE']}
                      title="Brokerage Settings"
                      exact
                      path="/org/settings"
                      component={OrgSettingsPage}
                    />
                    <PageRoute
                      title="Groups"
                      exact
                      path={[
                        '/users/groups/edit/:groupId',
                        '/users/groups/new',
                      ]}
                      component={NewGroupsPage}
                    />
                    {auth?.isAdmin &&
                      !isAdminAccessRestricted && ( // Prevents non-admin users from manually typing url and visiting page
                        <FeatureProtectedRoute
                          requiredFeatures={['MY_BROKERAGE']}
                          exact
                          path={[
                            '/users/list',
                            '/users/list/group/:groupId',
                            '/users/list/team/:teamId',
                          ]}
                          component={ManageUsersPage}
                        />
                      )}
                    {!isAdminAccessRestricted && (
                      <FeatureProtectedRoute
                        requiredFeatures={['MY_BROKERAGE']}
                        title="Admin"
                        exact
                        path="/admin"
                        component={AdminPage}
                      />
                    )}
                    <OrgAdminFeatureProtectedRoute
                      requiredFeatures={['ADMIN']}
                      title="Admin"
                      exact
                      path="/admin"
                      component={AdminPage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={['MY_BROKERAGE']}
                      exact
                      path="/admin/appointment_desk"
                      component={LiveAppointmentDesk}
                    />
                    <OrgAdminFeatureProtectedRoute
                      requiredFeatures={['ADMIN']}
                      title="Settings"
                      exact
                      path="/answering/settings"
                      component={AnsweringSettingsPage}
                    />
                    <PageRoute
                      title="Answering Service"
                      exact
                      path="/answering_service/user"
                      render={() => (
                        <AnsweringServicePage viewingLevel="user" />
                      )}
                    />
                    <PageRoute
                      title="Answering Service"
                      exact
                      path="/answering_service/org"
                      render={() => <AnsweringServicePage viewingLevel="org" />}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={['MLS_STAFF_MANAGE_USERS']}
                      title="Users"
                      exact
                      path={['/mls/users', '/mls/users/:page']}
                      render={props => (
                        <MLSStaffPage
                          {...props}
                          type="users"
                          key="mls-staff-users-page"
                        />
                      )}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={['MLS_STAFF_MANAGE_BROKERAGES']}
                      title="Brokerages"
                      exact
                      path={['/mls/brokerages', '/mls/brokerages/:page']}
                      render={props => (
                        <MLSStaffPage
                          {...props}
                          type="brokerages"
                          key="mls-staff-brokerages-page"
                        />
                      )}
                    />
                    {featureEnabled('quickLockboxAssignmentFeature') && (
                      <FeatureProtectedRoute
                        requiredFeatures={[
                          'MLS_STAFF_QUICK_LOCKBOX_ASSIGNMENT',
                        ]}
                        title="Quick Assignment"
                        exact
                        path="/mls/quick-assignment"
                        component={QuickLockboxAssignmentPage}
                      />
                    )}
                    {featureEnabled('quickLockboxAssignmentFeature') && (
                      <FeatureProtectedRoute
                        requiredFeatures={[
                          'MLS_STAFF_QUICK_LOCKBOX_ASSIGNMENT',
                        ]}
                        title="Quick Assignment Activity"
                        exact
                        path="/mls/quick-assignment-activity"
                        component={QuickLockboxAssignmentActivityPage}
                      />
                    )}
                    <FeatureProtectedRoute
                      requiredFeatures={['MLS_STAFF_VIEW_CONTRACTS']}
                      title="Active Contracts"
                      exact
                      path="/mls/contracts"
                      component={ContractsPage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={[]}
                      requiredConditions={[shouldDisplayAccessFeatures]}
                      title="Lockboxes"
                      exact
                      path={['/lockboxes']}
                      render={() => <LockboxesPage />}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={[]}
                      requiredConditions={[shouldDisplayAccessFeatures]}
                      title="Lockboxes"
                      exact
                      path={['/lockboxes/:lockboxType/:deviceId/accesslogs']}
                      // @ts-ignore when accessed through router, will get route params from useParams
                      render={() => <LockboxAccessLogPage />}
                    />
                    {useEnhancedAnswering && (
                      <PageRoute
                        title="Search"
                        exact
                        path={[
                          '/answering/search',
                          '/answering/search/brokerages',
                          '/answering/search/brokerages/:page',
                        ]}
                        render={props => (
                          <AnsweringSearchPage
                            {...props}
                            key="answering_search"
                            tab="brokerages"
                          />
                        )}
                      />
                    )}
                    {useEnhancedAnswering && (
                      <PageRoute
                        title="Search"
                        exact
                        path={[
                          '/answering/search/agents',
                          '/answering/search/agents/:page',
                        ]}
                        render={props => (
                          <AnsweringSearchPage
                            {...props}
                            key="answering_search"
                            tab="agents"
                          />
                        )}
                      />
                    )}
                    {/* Supra MVP Routes for MLS staff orgs */}
                    <FeatureProtectedRoute
                      requiredFeatures={[]}
                      requiredConditions={[shouldDisplayAccessFeatures]}
                      title="User"
                      key={0}
                      exact
                      path="/mls/user/lockboxesActivities"
                      component={LockBoxesActivityPage}
                    />
                    <PageRoute
                      title="User"
                      key={1}
                      exact
                      path="/mls/user/:userId"
                      component={MLSUserPage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={[]}
                      requiredConditions={[shouldDisplayAccessFeatures]}
                      title="User"
                      key={2}
                      exact
                      path="/mls/user/:userId/lockboxActivity"
                      component={LockBoxesActivityPage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={[]}
                      requiredConditions={[shouldDisplayAccessFeatures]}
                      title="Lockbox"
                      key={3}
                      exact
                      path="/mls/lockbox/:lockboxId"
                      component={SupraLockBoxDetailsPage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={[]}
                      requiredConditions={[shouldDisplayAccessFeatures]}
                      title="Lockbox"
                      key={4}
                      exact
                      path="/mls/lockboxesActivities"
                      component={LockBoxesActivityPage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={[]}
                      requiredConditions={[shouldDisplayAccessFeatures]}
                      title="Lockboxes"
                      key={5}
                      exact
                      path={['/mls/lockboxes', '/mls/lockboxes/:page']}
                      render={props => (
                        <MLSStaffPage
                          {...props}
                          type="lockboxes"
                          key="mls-staff-lockboxes-page"
                        />
                      )}
                    />
                    {/* Supra MVP Routes for brokerage users */}
                    <FeatureProtectedRoute
                      requiredFeatures={['SUPRA_MVP']}
                      requiredConditions={[shouldDisplayAccessFeatures]}
                      title="Lockbox"
                      exact
                      path="/lockbox/:lockboxId"
                      component={SupraLockBoxDetailsPage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={['SUPRA_MVP']}
                      requiredConditions={[shouldDisplayAccessFeatures]}
                      title="Lockbox"
                      exact
                      path={['/lockboxesActivities', '/lockboxesActivity']}
                      component={LockBoxesActivityPage}
                    />
                    <Route
                      exact
                      path="/contacts/:tabKey"
                      component={ContactsPage}
                    />
                    <Route
                      exact
                      path="/tours/:tourId"
                      component={TourEditPage}
                    />
                    {/* UserViewPage uses keys so that switching from one route to another will remount the component  */}
                    <FeatureProtectedRoute
                      requiredFeatures={['MY_BROKERAGE']}
                      exact
                      path={'/users/new'}
                      render={props => (
                        <UserViewPage key="new" isNew {...props} />
                      )}
                    />

                    <FeatureProtectedRoute
                      requiredFeatures={['MY_BROKERAGE']}
                      exact
                      path={'/users/edit/:userId'}
                      render={props => <UserViewPage key="edit" {...props} />}
                    />
                    <PageRoute
                      exact
                      title="Profile"
                      path={'/profile'}
                      render={props => (
                        <UserViewPage key="profile" isProfile {...props} />
                      )}
                    />
                    {[
                      UserRoles.Owner,
                      UserRoles.Manager,
                      UserRoles['Team Admin'],
                    ].includes(auth?.role) &&
                      auth?.team && (
                        <FeatureProtectedRoute
                          requiredFeatures={['TEAMS']}
                          exact
                          title="Team Settings"
                          path={'/team_settings'}
                          render={props => (
                            <TeamSettingsPage key="team_settings" {...props} />
                          )}
                        />
                      )}
                    <Route
                      exact
                      path={'/users/transfer/incoming/:transferId'}
                      render={props => (
                        <UserViewPage
                          key="incomingTransfer"
                          isIncomingTransfer
                          {...props}
                        />
                      )}
                    />
                    <Route
                      exact
                      path="/answering/manage-staff"
                      component={ManageAnsweringStaff}
                    />
                    {featureEnabled('supraAutoCoopFeature') && (
                      <Route
                        exact
                        path="/mls/coop/list"
                        component={MLSStaffCoopPage}
                      />
                    )}
                    <Route
                      exact
                      path="/answering/manage-deleted-staff"
                      render={props => (
                        <ManageAnsweringStaff deletedPage {...props} />
                      )}
                    />
                    <Route
                      exact
                      path="/feature-missing"
                      component={NoFeatureFlagPage}
                    />
                    <PageRoute
                      title="403"
                      exact
                      path="/403_team_account"
                      component={Error403TeamAccountPage}
                    />
                    <PageRoute
                      title="Book Multiple"
                      exact
                      path="/book_multiple"
                      component={BookMultipleExternalAppointments}
                    />
                    {isExternal && (
                      <PageRoute
                        title="Showing Details Page"
                        exact
                        path="/dash/:dashboardId/showing/:appointmentId"
                        component={ExternalShowingDetailsPage}
                      />
                    )}
                    {isExternal && (
                      <PageRoute
                        title="Showing Details Page"
                        exact
                        path="/rets/:retsId/showing/:appointmentId"
                        component={RetsShowingDetailsPage}
                      />
                    )}
                    {process.env.NODE_ENV === 'development' && (
                      <Route path="/dev" component={DevPage} />
                    )}
                    {isBrokerBay && (
                      // Temporary page for QA
                      <PageRoute
                        title="Add Users To Teams"
                        exact
                        path="/brokerbay_admin/add_users_to_teams"
                        render={() => <AddUsersToTeamsQAPage />}
                      />
                    )}
                    {isBrokerBay && (
                      // Temporary page for QA
                      <PageRoute
                        title="Add or Update Teams"
                        exact
                        path="/brokerbay_admin/add_or_update_teams"
                        render={() => <AddOrUpdateTeamsQAPage />}
                      />
                    )}
                    {isBrokerBay && (
                      // Temporary page for QA
                      <PageRoute
                        title="Onboarding Tools"
                        exact
                        path="/brokerbay_admin/onboarding_tools"
                        render={() => <OnboardingToolsPage />}
                      />
                    )}
                    {isBrokerBay && (
                      <PageRoute
                        title="QA Pages"
                        exact
                        path={[
                          '/brokerbay_admin/qa',
                          '/brokerbay_admin/qa/:state',
                        ]}
                        render={props => <QAPages {...props} />}
                      />
                    )}
                    <PageRoute
                      exact
                      title="Subscriptions and Payments"
                      path="/subscriptions_payments/:viewingLevel"
                      component={SubscriptionsPaymentsPage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={['MY_BROKERAGE']}
                      exact
                      path="/billing"
                      component={BillingInvoicesPage}
                    />
                    <FeatureProtectedRoute
                      requiredFeatures={['SUPRA_MVP']}
                      title="Cooperating Organizations"
                      exact
                      path="/coop/organizations"
                      component={coopOrganizationsPage}
                    />
                    <Route component={AngularPages} />
                  </Switch>
                </Content>
                <Footer />
              </div>
              <ContactDrawer />
            </StyledWrapper>
          )}
        </Route>
      </FeatureRedirects>
    </AppErrorBoundary>
  );
};

const StyledWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  > div {
    overflow-x: hidden;
  }
`;
